/**
 * Recovery from a stale code-split chunk after a deployment.
 *
 * ── What goes wrong ──────────────────────────────────────────────
 * Every route is `React.lazy`, so the entry HTML hard-codes hashed chunk names.
 * Cloudflare Pages serves one deployment at a time on the production alias: the
 * moment a new build goes live, the previous build's hashed files stop being
 * served. A browser still running the previous HTML — an open tab, a restored
 * session, a back-navigation — asks for a chunk that no longer exists.
 *
 * `public/_redirects` then makes it worse. The SPA fallback rewrites every
 * unmatched path to `/index.html` with status 200, so the missing chunk does
 * not 404: it returns an HTML document with `Content-Type: text/html`. The
 * browser refuses to evaluate that as a module and raises
 * "Failed to fetch dynamically imported module", which surfaces as a generic
 * application crash rather than as the cache-miss it actually is.
 *
 * ── The response ─────────────────────────────────────────────────
 * The underlying fix is in the caching headers. This module handles the client
 * that is already stale: it reloads the page exactly once so the browser
 * re-fetches the entry HTML and picks up the current chunk names.
 *
 * The guarantees that matter:
 *   - at most one reload per browsing session, so a genuinely broken deployment
 *     cannot put the user in a reload loop;
 *   - only for dynamic-import failures, never for ordinary runtime errors;
 *   - `localStorage` is never touched, so the business profile, catalog,
 *     customers, quotes and logo survive intact.
 */

/** Session-scoped, so it clears itself when the tab closes. */
export const RECOVERY_MARKER_KEY = 'quotylab:chunk-recovery';

/**
 * Messages browsers use when a module fails to load or parse. Chrome, Firefox
 * and Safari all word this differently, and Vite raises its own variant, so the
 * detection matches on any of them.
 */
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /unable to preload css/i,
  /importing a module script failed/i,
  /expected a javascript(?:-or-wasm)? module script/i,
  /'text\/html' is not a valid javascript mime type/i,
  /\bchunkloaderror\b/i,
  /loading chunk \d+ failed/i,
];

function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return '';
}

/**
 * True only for a failed dynamic import. An ordinary `TypeError` from
 * application code must never reach the reload path.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

interface RecoveryEnvironment {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
}

function defaultEnvironment(): RecoveryEnvironment | null {
  if (typeof window === 'undefined') return null;
  try {
    return {
      storage: window.sessionStorage,
      reload: () => window.location.reload(),
    };
  } catch {
    // Private browsing can refuse sessionStorage. Without somewhere to record
    // the attempt we cannot promise "at most once", so we decline to reload.
    return null;
  }
}

export function hasAlreadyRecovered(environment = defaultEnvironment()): boolean {
  if (!environment) return false;
  try {
    return environment.storage.getItem(RECOVERY_MARKER_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Reloads once to pick up the current entry HTML.
 *
 * Returns `true` when a reload was triggered, `false` when the caller should
 * fall through to the error UI — either because this session already tried, or
 * because there is no safe way to record the attempt.
 */
export function attemptChunkRecovery(environment = defaultEnvironment()): boolean {
  if (!environment) return false;
  if (hasAlreadyRecovered(environment)) return false;

  try {
    environment.storage.setItem(RECOVERY_MARKER_KEY, new Date().toISOString());
  } catch {
    return false;
  }

  environment.reload();
  return true;
}

/**
 * Recovers from a chunk failure if — and only if — that is what happened.
 * Returns `true` if a reload is under way and the caller should render nothing.
 */
export function recoverFromChunkError(error: unknown, environment = defaultEnvironment()): boolean {
  if (!isChunkLoadError(error)) return false;
  return attemptChunkRecovery(environment);
}

/**
 * Vite raises `vite:preloadError` when a dynamically imported chunk or its CSS
 * cannot be fetched. Catching it here recovers before React ever sees an error,
 * which covers imports made outside a component — the PDF renderer, for one.
 */
export function installChunkRecoveryListeners(target: Window = window): () => void {
  const onPreloadError = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (attemptChunkRecovery()) event.preventDefault();
    else console.error('[QuotyLab] Chunk preload failed and recovery was already used', payload);
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    if (attemptChunkRecovery()) event.preventDefault();
  };

  target.addEventListener('vite:preloadError', onPreloadError);
  target.addEventListener('unhandledrejection', onRejection);

  return () => {
    target.removeEventListener('vite:preloadError', onPreloadError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
